export interface ServiceProject {
  title: string;
  description: string;
  image?: string;
}

export interface ServiceDetail {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  overview: string;
  projects: ServiceProject[];
  technologies: string[];
  benefits: string[];
  heroImage?: string;
}

export const serviceDetails: Record<string, ServiceDetail> = {
  "b2b-solutions": {
    id: "b2b-solutions",
    title: "B2B Solutions",
    subtitle: "Corporate Mobility Partner",
    description: "Transform your business operations with our comprehensive B2B mobility solutions designed for modern enterprises.",
    overview: "Our B2B Solutions provide cutting-edge electric mobility for corporate campuses, warehouses, and hospitality sectors. We deliver sustainable transportation that enhances operational efficiency while reducing environmental impact. From last-mile logistics to executive transport, our fleet adapts to your unique business requirements.",
    projects: [
      {
        title: "Corporate Campus Mobility",
        description: "Deployed 50+ electric vehicles across major corporate campuses in Saudi Arabia, reducing internal transport time by 60%."
      },
      {
        title: "Warehouse & Logistics",
        description: "Integrated electric utility vehicles for warehouse operations, improving efficiency and eliminating indoor emissions."
      },
      {
        title: "Hotel & Resort Fleet",
        description: "Provided premium electric golf carts and utility vehicles for 5-star resorts, enhancing guest experience."
      }
    ],
    technologies: ["IoT Fleet Management", "GPS Tracking", "Predictive Maintenance", "Smart Charging", "Real-time Analytics"],
    benefits: ["Zero Emissions", "Reduced Operating Costs", "Enhanced Efficiency", "Sustainable Brand Image", "24/7 Support"]
  },
  "b2g-services": {
    id: "b2g-services",
    title: "B2G Services",
    subtitle: "Government & Public Sector",
    description: "Partner with government entities to deliver sustainable mobility solutions for public safety, healthcare, and smart city initiatives.",
    overview: "Our B2G Services support government agencies in achieving sustainability goals while improving public services. From municipal security patrols to public health emergency response, we provide reliable electric mobility that serves communities effectively. Our solutions align with Saudi Vision 2030 smart city objectives.",
    projects: [
      {
        title: "Municipal Security Patrols",
        description: "Equipped security forces with silent electric patrol vehicles for enhanced community surveillance."
      },
      {
        title: "Public Health Response",
        description: "Deployed iTank medical response vehicles for Dubai Ambulance, achieving 40% faster response times."
      },
      {
        title: "Smart City Integration",
        description: "Integrated electric mobility solutions with smart city infrastructure across multiple municipalities."
      }
    ],
    technologies: ["Emergency Response Systems", "Smart City APIs", "Real-time Dispatch", "Medical Equipment Integration", "Secure Communications"],
    benefits: ["Faster Response Times", "Silent Operations", "Community Safety", "Vision 2030 Aligned", "Reduced Carbon Footprint"]
  },
  "leasing-mobility": {
    id: "leasing-mobility",
    title: "Leasing Programs",
    subtitle: "On-Demand Fleet Leasing",
    description: "Flexible leasing options for events, seasonal operations, and temporary deployments without capital investment.",
    overview: "Our Leasing Programs provide flexible access to premium electric vehicles without long-term commitments. Ideal for major events, exhibitions, seasonal tourism, and temporary operational needs. We handle everything from delivery to on-site support, ensuring seamless mobility for your special occasions.",
    projects: [
      {
        title: "Formula 1 Event Support",
        description: "Provided N13 MAX vehicles for Jeddah F1 Track marshals, ensuring rapid response across the circuit."
      },
      {
        title: "Exhibition & Trade Shows",
        description: "Supplied electric mobility for major exhibitions including NEOM showcase events."
      },
      {
        title: "Seasonal Tourism",
        description: "Deployed seasonal fleets for tourism hotspots during peak seasons across the Kingdom."
      }
    ],
    technologies: ["Quick Deployment", "Event Management Systems", "Temporary Infrastructure", "On-site Charging", "Real-time Support"],
    benefits: ["No Capital Investment", "Flexible Terms", "Full Support Included", "Brand Customization", "Scalable Fleet Size"]
  },
  "service-maintenance": {
    id: "service-maintenance",
    title: "Service & Maintenance",
    subtitle: "Authorized Service Provider",
    description: "Comprehensive after-sales support, repairs, spare parts, and technical maintenance from certified technicians.",
    overview: "As an authorized service provider, we deliver comprehensive maintenance, repairs, and technical support for all mobility assets. Our certified technicians ensure maximum uptime and operational reliability. From preventive maintenance programs to emergency repairs, we keep your fleet running at peak performance.",
    projects: [
      {
        title: "Fleet Maintenance Contracts",
        description: "Managing preventive maintenance for 200+ vehicles across multiple corporate clients with 99% uptime."
      },
      {
        title: "Emergency Repair Network",
        description: "Established 24/7 emergency repair capability with on-site response within 4 hours across major cities."
      },
      {
        title: "Technical Training Academy",
        description: "Trained over 150 technicians in electric vehicle maintenance and safety protocols."
      }
    ],
    technologies: ["Diagnostic Systems", "Preventive Maintenance AI", "Spare Parts Inventory", "Mobile Service Units", "Remote Monitoring"],
    benefits: ["24/7 Emergency Support", "Genuine Spare Parts", "Certified Technicians", "On-site Service", "Extended Warranty Programs"]
  },
  "fleet-customization": {
    id: "fleet-customization",
    title: "Fleet Customization",
    subtitle: "Modification & Branding",
    description: "Transform vehicles into purpose-built solutions with custom branding, livery, and equipment integration.",
    overview: "Our Fleet Customization services transform standard vehicles into purpose-built solutions tailored to your exact operational requirements. From custom branding and corporate livery to specialized equipment integration, we modify and adapt mobility assets to enhance your brand presence and operational capability.",
    projects: [
      {
        title: "Emergency Vehicle Conversion",
        description: "Converted electric vehicles into fully-equipped emergency response units with medical equipment and sirens."
      },
      {
        title: "Corporate Fleet Branding",
        description: "Designed and applied custom livery for 100+ vehicle corporate fleets, enhancing brand visibility."
      },
      {
        title: "Security Vehicle Integration",
        description: "Integrated communication systems, cameras, and patrol equipment into security fleet vehicles."
      }
    ],
    technologies: ["Custom Fabrication", "Vehicle Wrapping", "Electronic Integration", "Safety Certification", "Design Consultation"],
    benefits: ["Custom Branding", "Equipment Integration", "Purpose-Built Solutions", "Enhanced Functionality", "Professional Finish"]
  },
  "motorized-mobility": {
    id: "motorized-mobility",
    title: "Motorized Mobility",
    subtitle: "Electric & Combustion",
    description: "Complete range of motorized mobility spanning electric and combustion-powered vehicles for diverse operational needs.",
    overview: "Our Motorized Mobility division offers a complete range of vehicles spanning both electric and combustion-powered options. From zero-emission electric fleets for urban operations to heavy-duty combustion vehicles for demanding industrial environments, we provide the right mobility solution for every operational requirement.",
    projects: [
      {
        title: "Industrial Site Fleet",
        description: "Deployed mixed electric and combustion fleet for large-scale industrial site operations requiring heavy-duty capability."
      },
      {
        title: "Urban Delivery Network",
        description: "Established zero-emission electric delivery fleet serving urban centers with strict environmental requirements."
      },
      {
        title: "Construction Site Mobility",
        description: "Provided rugged combustion and hybrid vehicles for challenging construction site environments."
      }
    ],
    technologies: ["Electric Powertrains", "Combustion Engines", "Hybrid Systems", "Heavy-Duty Platforms", "Fleet Integration"],
    benefits: ["Electric Fleet Options", "Combustion Capability", "Hybrid Solutions", "Industrial Grade", "Diverse Applications"]
  },
  "mega-projects": {
    id: "mega-projects",
    title: "Mega Projects",
    subtitle: "Run & Operate",
    description: "Complete operational management for large-scale mobility deployments at mega events and smart cities.",
    overview: "Our Mega Projects division provides end-to-end operational management for large-scale mobility deployments. From initial project design and fleet deployment to staff training and real-time operations centers, we manage every aspect of major mobility infrastructure for mega events, smart cities, and large facilities.",
    projects: [
      {
        title: "Smart City Mobility Hub",
        description: "Designed and operate complete mobility infrastructure for a new smart city development with 500+ vehicles."
      },
      {
        title: "Major Sporting Event Operations",
        description: "Managed complete mobility operations for international sporting events serving 50,000+ daily visitors."
      },
      {
        title: "Airport Ground Transportation",
        description: "Operating comprehensive ground transportation system for major international airport with 24/7 service."
      }
    ],
    technologies: ["Operations Center", "Real-time Dispatch", "Staff Management Systems", "Performance Analytics", "Integration APIs"],
    benefits: ["End-to-End Management", "Scalable Operations", "Trained Staff", "Real-time Monitoring", "Performance Guarantees"]
  }
};
